import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/components/feature/AuthProvider";
import { useClub } from "@/hooks/useClub";
import { useStepMe } from "@/hooks/useStepMe";
import { useStepRankPreview } from "@/hooks/useStepRankPreview";
import { isApp, stepBridgeReady, stepRequestPermission, stepSupported } from "@/lib/mdlApp";
import { formatKm, formatNumber, hasRank, medalFor } from "@/pages/steps/utils";
import StepGauge from "./StepGauge";
import StepWeekBars from "./StepWeekBars";

type ViewState =
  | "loading"
  | "error"
  | "empty"
  | "outdated"
  | "sensor"
  | "permission"
  | "starting"
  | "normal";

// 메인 「내 만보계」 카드.
// sections.step 이 켜져 있으면 로그인 여부와 상관없이 표시한다.
//  - 로그인 회원: 실시간 걸음 요약 (앱 안이면 3초마다 센서값으로 갱신)
//  - 비로그인   : 기능 소개 + 오늘 랭킹 맛보기 + 로그인/랭킹 버튼
export default function StepCard() {
  const { member, openLogin } = useAuth();
  const { club } = useClub();
  const cardRef = useRef<HTMLDivElement>(null);

  // step_me.php 는 로그인이 필요하므로 enabled=!!member 로만 호출된다.
  const { data, loading, error, steps, permitted, reload } = useStepMe(!!member, cardRef);
  // 비로그인일 때만 오늘 랭킹 맛보기를 가져온다.
  const preview = useStepRankPreview(!member);

  const app = club?.app;
  const appReady = Number(app?.has ?? 0) === 1 && !!app?.url;
  const memo = app?.memo || "걸음 수는 안드로이드 앱에서 자동으로 기록됩니다";

  const appNow = isApp();
  const bridgeReady = stepBridgeReady();
  const supported = stepSupported();

  let view: ViewState = "normal";
  if (loading) view = "loading";
  else if (error) view = "error";
  else if (!data) view = "empty";
  else if (appNow && !bridgeReady) view = "outdated";
  else if (appNow && bridgeReady && !supported) view = "sensor";
  else if (appNow && bridgeReady && supported && !permitted) view = "permission";
  // steps = 서버값과 센서값 중 큰 쪽 → 센서에 걸음이 하나라도 있으면 바로 게이지를 보여주고,
  // 서버 동기화는 뒤에서 따라오게 한다.
  else if (appNow && Number(data.has_app) === 0 && steps === 0)
    view = "starting";

  const rank = Number(data?.today?.rank ?? 0);
  const medal = medalFor(rank);
  const goal = Number(data?.goal ?? 0);
  const showGauge = view === "normal";

  // 왼쪽 영역 — 게이지 또는 상태 안내 (로그인 회원 전용)
  let left: ReactNode = null;
  if (view === "loading") {
    left = <div className="mx-auto h-40 w-40 animate-pulse rounded-full bg-background-200" aria-hidden="true"></div>;
  } else if (view === "outdated") {
    left = (
      <StateBox
        icon="ri-download-cloud-2-line"
        title="앱 업데이트가 필요합니다"
        desc="만보계는 최신 버전 앱에서 동작합니다. 앱을 새로 설치해 주세요."
        action={
          appReady ? (
            <a
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
            >
              <i className="ri-download-2-line" aria-hidden="true"></i>
              앱 다시 설치
            </a>
          ) : undefined
        }
      />
    );
  } else if (view === "sensor") {
    left = (
      <StateBox
        icon="ri-footprint-line"
        title="이 기기는 걸음 센서를 지원하지 않습니다"
        desc="걸음 기록은 센서가 있는 안드로이드 앱에서만 쌓입니다."
      />
    );
  } else if (view === "permission") {
    left = (
      <StateBox
        icon="ri-shield-keyhole-line"
        title="걸음 수 측정을 허용해 주세요"
        desc="허용하면 지금부터 걸음이 쌓입니다."
        action={
          <button
            type="button"
            onClick={() => stepRequestPermission()}
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            허용하기
          </button>
        }
      />
    );
  } else if (view === "starting") {
    left = (
      <StateBox
        icon="ri-walk-line"
        title="측정을 시작했습니다"
        desc="잠시 후부터 걸음이 쌓입니다. 설치한 날은 설치 시점부터 세므로 숫자가 적은 게 정상이에요."
      />
    );
  } else if (view === "normal") {
    left = <StepGauge steps={steps} goal={goal} />;
  }

  return (
    <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-16">
      <div className="mx-auto max-w-6xl">
        <div
          ref={cardRef}
          className="rounded-2xl border border-background-200 bg-background-50 p-5 md:p-7"
        >
          {/* 헤더 */}
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span aria-hidden="true">🏃</span>
              내 만보계
            </h2>
            <Link
              to="/steps"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              {/* 순위 뱃지는 로그인 회원에게만 노출 */}
              {member && hasRank(rank) ? (
                <>
                  오늘 {medal ? `${medal} ` : ""}
                  {rank}위
                </>
              ) : (
                <>걸음 랭킹</>
              )}
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>

          {!member ? (
            /* ── 비로그인 ─────────────────────────────── */
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
              <div className="w-full md:w-56">
                <GuestLock />
              </div>

              <div className="min-w-0 space-y-5">
                <ul className="space-y-2.5">
                  <IntroLine icon="ri-footprint-line" text="걸음 수가 자동으로 기록됩니다" />
                  <IntroLine
                    icon="ri-trophy-line"
                    text="회원들과 오늘 · 이번주 · 이번달 랭킹을 겨룹니다"
                  />
                  <IntroLine
                    icon="ri-smartphone-line"
                    text="안드로이드 앱만 설치하면 끝. 따로 할 일이 없습니다"
                  />
                </ul>

                {preview.rows.length > 0 && (
                  <div className="rounded-xl border border-background-200 bg-background-100 px-4 py-3.5">
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground-600">
                      <i className="ri-fire-line text-accent-600" aria-hidden="true"></i>
                      오늘 {formatNumber(preview.players)}명이 걷고 있습니다
                    </p>
                    <ul className="mt-2.5 space-y-1.5">
                      {preview.rows.map((r) => (
                        <li key={r.mem_no} className="flex items-center gap-2 text-sm">
                          <span className="w-5 shrink-0 text-center" aria-hidden="true">
                            {medalFor(r.rank) ?? r.rank}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-foreground-800">
                            {r.aho ? `${r.aho} ` : ""}
                            {r.name}
                          </span>
                          <span className="shrink-0 font-heading text-sm font-bold tabular-nums text-foreground-950">
                            {formatNumber(r.steps)}
                            <span className="ml-0.5 text-[11px] font-normal text-foreground-500">
                              보
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={openLogin}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-login-circle-line" aria-hidden="true"></i>
                    로그인하고 시작하기
                  </button>
                  <Link
                    to="/steps"
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    전체 랭킹 보기
                    <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                  </Link>
                </div>

                <AppInstallNote appNow={appNow} appReady={appReady} url={app?.url} memo={memo} />
              </div>
            </div>
          ) : view === "error" ? (
            /* ── 로그인 · 오류 ─────────────────────────── */
            <div className="rounded-xl border border-dashed border-background-300 px-6 py-10 text-center">
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            </div>
          ) : (
            /* ── 로그인 · 정상 ─────────────────────────── */
            <div className="grid gap-6 md:grid-cols-[auto_1fr] md:items-center">
              {/* 왼쪽 — 게이지 / 상태 */}
              <div className="w-full md:w-56">{left}</div>

              {/* 오른쪽 — 통계 + 7일 막대 + 주·월 요약 */}
              <div className="min-w-0 space-y-5">
                {showGauge && (
                  <>
                    <div className="grid grid-cols-3 divide-x divide-background-200 rounded-xl border border-background-200 bg-background-100">
                      <Stat label="거리" value={`${formatKm(data?.today?.km)}km`} />
                      <Stat label="칼로리" value={`${formatNumber(data?.today?.kcal)}kcal`} />
                      <Stat label="참여" value={`${formatNumber(data?.today?.players)}명`} />
                    </div>

                    {data && data.week7.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold text-foreground-500">최근 7일</p>
                        <StepWeekBars days={data.week7} />
                      </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-foreground-600">
                        <span>
                          이번주{" "}
                          <strong className="font-bold tabular-nums text-foreground-950">
                            {formatNumber(data?.week?.steps)}보
                          </strong>
                          {hasRank(data?.week?.rank) ? ` ${data?.week?.rank}위` : ""}
                        </span>
                        <span>
                          이번달{" "}
                          <strong className="font-bold tabular-nums text-foreground-950">
                            {hasRank(data?.month?.rank)
                              ? `${data?.month?.rank}위`
                              : "기록 없음"}
                          </strong>
                        </span>
                      </div>
                      <Link
                        to="/steps"
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
                      >
                        전체 랭킹 보기
                        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                      </Link>
                    </div>
                  </>
                )}

                {view !== "loading" && (
                  <AppInstallNote appNow={appNow} appReady={appReady} url={app?.url} memo={memo} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// 비로그인 왼쪽 — «잠긴» 플레이스홀더 (가짜 걸음 수를 보여주지 않는다)
function GuestLock() {
  return (
    <div className="relative mx-auto h-40 w-40 shrink-0">
      <div
        className="absolute inset-0 rounded-full border-2 border-dashed border-background-300 bg-background-100"
        aria-hidden="true"
      ></div>
      <div className="absolute inset-[10px] flex flex-col items-center justify-center rounded-full bg-background-100 text-center">
        <span className="font-heading text-3xl font-bold tracking-widest text-foreground-300">
          - - -
        </span>
        <span className="text-xs text-foreground-400">걸음</span>
        <span className="mt-1 flex items-center gap-1 px-4 text-[11px] font-semibold text-foreground-500">
          <i className="ri-lock-line" aria-hidden="true"></i>
          로그인 후 이용 가능합니다
        </span>
      </div>
    </div>
  );
}

function IntroLine({ icon, text }: { icon: string; text: string }) {
  return (
    <li className="flex items-center gap-3 text-sm text-foreground-700">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-50 text-base text-primary-600">
        <i className={icon} aria-hidden="true"></i>
      </span>
      {text}
    </li>
  );
}

function AppInstallNote({
  appNow,
  appReady,
  url,
  memo,
}: {
  appNow: boolean;
  appReady: boolean;
  url?: string;
  memo: string;
}) {
  // 앱 안에서는 이미 설치된 상태라 숨긴다 (브라우저에서만 보인다)
  if (appNow) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-background-100 px-4 py-3">
      <p className="flex items-center gap-2 text-xs text-foreground-500">
        <i className="ri-information-line" aria-hidden="true"></i>
        {memo}
      </p>
      {appReady && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-background-50 px-4 py-2 text-xs font-semibold text-primary-700 hover:bg-primary-50"
        >
          <i className="ri-android-line" aria-hidden="true"></i>
          앱 설치 안내
        </a>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-3.5 text-center">
      <p className="text-[11px] text-foreground-500">{label}</p>
      <p className="mt-0.5 font-heading text-sm font-bold tabular-nums text-foreground-900">
        {value}
      </p>
    </div>
  );
}

function StateBox({
  icon,
  title,
  desc,
  action,
}: {
  icon: string;
  title: string;
  desc?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 px-4 text-center md:w-56">
      <span className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-background-50 text-xl text-primary-600">
        <i className={icon} aria-hidden="true"></i>
      </span>
      <p className="font-heading text-sm font-bold text-foreground-900">{title}</p>
      {desc && <p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{desc}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}